import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styles from './AppointmentManagement.module.css';
import {
  Appointment,
  cancelAppointment,
  getScheduledAppointments,
  InsuranceInfo,
  uploadInsuranceCardWeb,
} from 'externalAPIs/insurance-eligibility';
import {useMagicLinkVerification} from '../hooks/useMagicLinkVerification';
import {FullScreenLoadingSpinner} from 'components/FullScreenLoadingSpinner/FullScreenLoadingSpinner';
import {ActiveAppointmentSection} from '../components/ActiveAppointmentSection';
import {ActiveAppointmentStatus} from '../components/ActiveAppointmentStatus';
import {AppointmentItem} from '../components/AppointmentItem';
import {NoAppointmentsState} from '../components/NoAppointmentsState';
import {InsuranceCardUpload} from '../components/InsuranceCardUpload';
import {ExpiredLinkScreen} from '../components/ExpiredLinkScreen';
import {getAppointmentStatus, STATUS_TYPES, getPersonIdFromToken} from '../utils';
import {CancelIcon, MessageIcon, InsuranceIcon, RescheduleIcon} from '../icons';
import {ConfirmCancelDialog} from '../components/ConfirmCancelDialog';
import Analytics from 'utils/Analytics';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import {setValueInSurveyData} from 'utils/setValueInSurveyData';

interface AppointmentManagementProps {
  status?: 'pending' | 'confirmed';
}

export const appointmentSort = (a: Appointment, b: Appointment) => {
  const aTime = a?.startTime ? new Date(a?.startTime).getTime() : 0;
  const bTime = b?.startTime ? new Date(b?.startTime).getTime() : 0;
  return aTime - bTime;
};

export const appointmentSortReverse = (a: Appointment, b: Appointment) => {
  const aTime = a?.startTime ? new Date(a?.startTime).getTime() : 0;
  const bTime = b?.startTime ? new Date(b?.startTime).getTime() : 0;
  return bTime - aTime;
};

const SURVEY_KEY_FRONT = 'insurance_card_front_uploaded';
const SURVEY_KEY_BACK = 'insurance_card_back_uploaded';

const AppointmentManagement: React.FC<AppointmentManagementProps> = () => {
  const {accessToken, isLoading: isVerifying} = useMagicLinkVerification();

  // Decode personId from JWT token
  const personId = useMemo(() => getPersonIdFromToken(accessToken), [accessToken]);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [insuranceInfo, setInsuranceInfo] = useState<InsuranceInfo | undefined>(undefined);
  const [tags, setTags] = useState<string[]>([]);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<Appointment | null>(null);
  const [isCanceling, setIsCanceling] = useState<boolean>(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showInsuranceSection, setShowInsuranceSection] = useState<boolean>(false);

  // Initialize from survey data
  const [uploadedFrontFile, setUploadedFrontFile] = useState<File | null>(() => {
    const surveyData = SurveyData.read();
    if (surveyData?.[SURVEY_KEY_FRONT]) {
      return new File([], 'front.jpg');
    }
    return null;
  });
  const [uploadedBackFile, setUploadedBackFile] = useState<File | null>(() => {
    const surveyData = SurveyData.read();
    if (surveyData?.[SURVEY_KEY_BACK]) {
      return new File([], 'back.jpg');
    }
    return null;
  });

  // Initialize uploaded files from server status when data first loads
  useEffect(() => {
    if (
      !isLoading &&
      (insuranceInfo?.cardUploadStatus === 'uploaded' || insuranceInfo?.cardUploadStatus === 'verified')
    ) {
      // Server confirms both cards are uploaded - clear survey data since server has them
      setValueInSurveyData(SURVEY_KEY_FRONT, undefined);
      setValueInSurveyData(SURVEY_KEY_BACK, undefined);
      // Create placeholder files to indicate both cards are uploaded on server
      // Only set if not already set (to avoid overwriting user uploads)
      setUploadedFrontFile(prev => prev ?? new File([], 'front-from-server.jpg'));
      setUploadedBackFile(prev => prev ?? new File([], 'back-from-server.jpg'));
    }
  }, [isLoading, insuranceInfo?.cardUploadStatus]);

  const bothUploadedOnServer =
    insuranceInfo?.cardUploadStatus === 'uploaded' || insuranceInfo?.cardUploadStatus === 'verified';

  const {activeAppointment, upcomingAppointments, pastAppointments} = useMemo(() => {
    const now = new Date();
    const upcoming: Appointment[] = [];
    const past: Appointment[] = [];

    appointments.forEach(appointment => {
      const startTime = appointment?.startTime ? new Date(appointment.startTime) : undefined;

      // Check if appointment is cancelled - add to past sessions
      if (appointment.status === 'Cancelled') {
        past.push(appointment);
        return;
      }

      // Categorize non-cancelled appointments by time
      if (startTime) {
        if (startTime > now || appointment.status === 'Upcoming') {
          upcoming.push(appointment);
        } else {
          past.push(appointment);
        }
      } else if (appointment.status === 'Upcoming') {
        upcoming.push(appointment);
      } else {
        past.push(appointment);
      }
    });

    const sortedUpcoming = upcoming.sort(appointmentSort);
    const activeAppointment = sortedUpcoming.length > 0 ? sortedUpcoming[0] : undefined;
    const remainingUpcoming = sortedUpcoming.slice(1);

    return {
      activeAppointment,
      upcomingAppointments: remainingUpcoming,
      pastAppointments: past.sort(appointmentSortReverse),
    };
  }, [appointments]);

  // Determine if insurance card is pending (needs upload)
  // Badge should only disappear when BOTH files are uploaded (or server confirms both uploaded)
  const activeAppointmentStatus = activeAppointment
    ? getAppointmentStatus(activeAppointment, tags, insuranceInfo?.cardUploadStatus ?? '')
    : null;
  const isInsurancePending =
    activeAppointmentStatus !== STATUS_TYPES.ELIGIBLE &&
    (insuranceInfo?.cardUploadStatus === 'missing' ||
      ((!uploadedFrontFile || !uploadedBackFile) && !bothUploadedOnServer));

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({top: 0, left: 0, behavior: 'auto'});
    }
  }, []);

  const fetchAppointments = useCallback(async () => {
    // Wait for verification to complete
    if (isVerifying) {
      return;
    }

    // If no access token available, don't fetch appointments
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      if (!personId) {
        console.error('PersonId not available from token');
        setAppointments([]);
        return;
      }
      const appointmentResponse = await getScheduledAppointments(personId, accessToken);
      setAppointments(appointmentResponse.appointments);
      setInsuranceInfo(appointmentResponse.insuranceInfo);
      setTags(appointmentResponse.tags ?? []);
    } catch (error) {
      console.error('Error fetching appointments', error);
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  }, [isVerifying, accessToken, personId]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleCardUpload = async (frontFile?: File, backFile?: File) => {
    if (!frontFile && !backFile) {
      return;
    }

    if (!accessToken) {
      throw new Error('Access token is required');
    }

    if (!personId) {
      throw new Error('PersonId not available from token');
    }

    try {
      await uploadInsuranceCardWeb(personId, frontFile, backFile, accessToken);

      // Track uploaded files locally and persist to survey data
      if (frontFile) {
        setUploadedFrontFile(frontFile);
        setValueInSurveyData(SURVEY_KEY_FRONT, true);
      }
      if (backFile) {
        setUploadedBackFile(backFile);
        setValueInSurveyData(SURVEY_KEY_BACK, true);
      }

      // Refetch appointments to get updated insurance info (without showing full loading spinner)
      try {
        if (!personId) {
          console.error('PersonId not available from token');
          return;
        }
        const appointmentResponse = await getScheduledAppointments(personId, accessToken);
        setAppointments(appointmentResponse.appointments);
        setInsuranceInfo(appointmentResponse.insuranceInfo);
        setTags(appointmentResponse.tags ?? []);
      } catch (error) {
        console.error('Error refetching appointments', error);
      }

      // Track successful insurance card upload
      const uploadSides = [];
      if (frontFile) uploadSides.push('front');
      if (backFile) uploadSides.push('back');
      Analytics.track('Insurance Card Uploaded', {
        sides: uploadSides.join(', '),
        personId,
      });
    } catch (error) {
      console.error('Error uploading insurance card:', error);
      throw error;
    }
  };

  const handleCancelClick = (appointment: Appointment) => {
    setAppointmentToCancel(appointment);
    setCancelError(null);
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel) return;

    if (!accessToken) {
      setCancelError('Access token is required to cancel appointment');
      return;
    }

    setIsCanceling(true);
    setCancelError(null);
    try {
      if (!personId) {
        setCancelError('Unable to identify your account. Please try again.');
        return;
      }
      await cancelAppointment(personId, appointmentToCancel.appointmentId, accessToken);
      // Refetch appointments to get updated list
      await fetchAppointments();

      // Track successful appointment cancellation
      Analytics.track('Appointment Canceled', {
        appointmentId: appointmentToCancel.appointmentId,
        appointmentStartTime: appointmentToCancel.startTime,
        appointmentStatus: appointmentToCancel.status,
        personId,
      });
      setShowCancelDialog(false);
      setAppointmentToCancel(null);
      setCancelError(null);
    } catch (error) {
      console.error('Error canceling appointment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel appointment. Please try again.';
      setCancelError(errorMessage);
    } finally {
      setIsCanceling(false);
    }
  };

  const handleCancelDialog = () => {
    setShowCancelDialog(false);
    setAppointmentToCancel(null);
  };

  if (isVerifying || isLoading) {
    return (
      <div className={styles.page} data-testid="screen-appointment-management">
        <FullScreenLoadingSpinner message={isVerifying ? 'Verifying access...' : 'Loading appointments...'} />
      </div>
    );
  }

  // If no access token available, show expired link screen
  // This covers cases where:
  // - No token exists and no email/code to verify
  // - Verification failed (token couldn't be obtained)
  if (!accessToken) {
    return (
      <div className={styles.page} data-testid="screen-appointment-management">
        <ExpiredLinkScreen />
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className={styles.page} data-testid="screen-appointment-management">
        <NoAppointmentsState />
      </div>
    );
  }

  return (
    <div className={styles.page} data-testid="screen-appointment-management">
      {/* Active Appointment Section */}
      {activeAppointment ? (
        <>
          <ActiveAppointmentSection appointment={activeAppointment} />

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => {
                window.location.href = 'sms:+13123135723';
              }}
            >
              <MessageIcon />
              <span>Message</span>
            </button>
            <button
              type="button"
              className={`${styles.actionButton} ${isInsurancePending ? styles.actionButtonHighlighted : ''}`}
              onClick={() => {
                setShowInsuranceSection(!showInsuranceSection);
              }}
            >
              <InsuranceIcon />
              <span>Insurance</span>
              {isInsurancePending ? <span className={styles.actionButtonBadge}>1</span> : null}
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => {
                const message = encodeURIComponent(
                  "Hello, I'd like to reschedule my upcoming sleep expert appointment. Could you send me some available times?",
                );
                window.location.href = `sms:+13123135723?body=${message}`;
              }}
            >
              <RescheduleIcon />
              <span>Reschedule</span>
            </button>
          </div>

          {/* Insurance Card Upload - show when expanded */}
          {showInsuranceSection && (
            <div className={styles.insuranceCardSection}>
              <div className={styles.sectionHeader}>
                <div>
                  <h2 className={styles.sectionTitle}>Insurance card</h2>
                  <p className={styles.sectionDescription}>
                    Upload photos of both sides of your insurance card to verify your insurance benefits.
                  </p>
                </div>
              </div>
              <InsuranceCardUpload
                onUpload={handleCardUpload}
                uploadStatus={insuranceInfo?.cardUploadStatus}
                frontFile={uploadedFrontFile}
                backFile={uploadedBackFile}
              />
              {uploadedFrontFile && uploadedBackFile && !bothUploadedOnServer ? (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={() => {
                    setValueInSurveyData(SURVEY_KEY_FRONT, undefined);
                    setValueInSurveyData(SURVEY_KEY_BACK, undefined);
                    setUploadedFrontFile(null);
                    setUploadedBackFile(null);
                  }}
                >
                  Re-upload card photos
                </button>
              ) : null}
            </div>
          )}

          <ActiveAppointmentStatus
            appointment={activeAppointment}
            tags={tags}
            cardUploadStatus={insuranceInfo?.cardUploadStatus ?? ''}
          />

          {activeAppointment.status !== 'Cancelled' && (
            <div className={styles.appointmentItemActions}>
              <button
                className={styles.appointmentActionLink}
                type="button"
                onClick={() => handleCancelClick(activeAppointment)}
                disabled={isCanceling}
              >
                <CancelIcon />
                <span>Cancel my session</span>
              </button>
            </div>
          )}
        </>
      ) : null}

      {/* Upcoming Sessions Section */}
      {upcomingAppointments.length > 0 ? (
        <div className={styles.sessionsSection}>
          {activeAppointment ? <div className={styles.sectionDivider} /> : null}
          <h2 className={styles.sectionTitle}>
            {upcomingAppointments.length === 1 ? 'Upcoming session' : 'Upcoming sessions'}
          </h2>
          <div className={styles.appointmentsList}>
            {upcomingAppointments.map(appointment => {
              const appointmentStatus = getAppointmentStatus(appointment, tags, insuranceInfo?.cardUploadStatus ?? '');
              return (
                <AppointmentItem
                  key={appointment.appointmentId}
                  appointment={appointment}
                  isUpcoming={true}
                  status={appointmentStatus}
                  onCancelClick={handleCancelClick}
                  isCanceling={isCanceling}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Past Sessions Section */}
      {pastAppointments.length > 0 ? (
        <>
          {activeAppointment || upcomingAppointments.length > 0 ? <div className={styles.sectionDivider} /> : null}
          <div className={styles.sessionsSection}>
            <h2 className={styles.sectionTitle}>{pastAppointments.length === 1 ? 'Past session' : 'Past sessions'}</h2>
            <div className={styles.appointmentsList}>
              {pastAppointments.map(appointment => {
                const isCancelled = appointment.status === 'Cancelled';
                return (
                  <AppointmentItem
                    key={appointment.appointmentId}
                    appointment={appointment}
                    isUpcoming={false}
                    status={isCancelled ? STATUS_TYPES.CANCELLED : undefined}
                  />
                );
              })}
            </div>
          </div>
        </>
      ) : null}

      <ConfirmCancelDialog
        isOpen={showCancelDialog}
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelDialog}
        isCanceling={isCanceling}
        error={cancelError}
      />
    </div>
  );
};

export default AppointmentManagement;
